import React, { useState } from 'react';
import {
  CreativeFatigueEntry,
  PlatformSignalEntry,
  PLATFORM_LABELS,
  SignalsPayload,
  getSignals,
} from '../../../services/cgBoostApi';
import { ViewDataBoundary } from '../useViewData';
import { cardStyle, flagToStatus, sectionTitle } from '../styles';
import { StatusPill } from '../../ui';

interface Props { campaignId: string; }

const SignalsView: React.FC<Props> = ({ campaignId }) => (
  <ViewDataBoundary
    loader={getSignals}
    campaignId={campaignId}
    loadingLabel="Loading signals…"
    render={(data) => <SignalsBody data={data} />}
  />
);

const SignalsBody: React.FC<{ data: SignalsPayload }> = ({ data }) => {
  return (
    <>
      <SignalCard
        index={1}
        title="Content Resonance Score"
        question="Which asset held viewer attention most effectively?"
        formula="(VTR + Watch Completion Rate) ÷ 2 per asset"
        plainLanguage={data.contentResonance[0]?.plainLanguage ?? 'No data'}
        rows={data.contentResonance.map((e) => ({
          label: `${e.format} — ${e.creatorName} (${e.hookStyle})`,
          value: `${(e.score * 100).toFixed(1)}%`,
          flag: e.flag === 'top' ? { variant: 'top', label: 'Top Asset' } : e.flag === 'hookStrength' ? { variant: 'info', label: 'Hook Strength' } : null,
        }))}
      />

      <SignalCard
        index={2}
        title="Audience Action Rate"
        question="Which platform drove the most viewer response?"
        formula="Total Clicks ÷ Total Views per platform"
        plainLanguage={data.audienceActionRate[0]?.plainLanguage ?? 'No data'}
        rows={data.audienceActionRate.map((e) => platformRow(e, (v) => `${(v * 100).toFixed(2)}%`))}
      />

      <SignalCard
        index={3}
        title="Reach Efficiency"
        question="Which platform delivered the most reach per dollar?"
        formula="Total Impressions ÷ Total Spend per platform"
        plainLanguage={data.reachEfficiency[0]?.plainLanguage ?? 'No data'}
        rows={data.reachEfficiency.map((e) => platformRow(e, (v) => `${v.toLocaleString()} imp/$`))}
      />

      <SignalCard
        index={4}
        title="Creative Fatigue Signal"
        question="Whether creative needs rotation"
        formula="Week 1 CTR vs Week 2 CTR per asset; refresh + frequency triggers"
        plainLanguage={data.refreshWindow.plainLanguage}
        rows={[
          ...data.creativeFatigue.map((e: CreativeFatigueEntry) => ({
            label: `${e.creatorName} (${e.format})`,
            value: `W1 ${(e.week1Ctr * 100).toFixed(2)}% → W2 ${(e.week2Ctr * 100).toFixed(2)}%`,
            flag: e.flag === 'fatigue'
              ? { variant: 'warn' as const, label: 'Fatigue' }
              : e.flag === 'momentum'
                ? { variant: 'top' as const, label: 'Momentum' }
                : null,
          })),
          {
            label: 'Refresh window',
            value: `peak weekly spend $${Math.round(data.refreshWindow.weeklySpendUsd).toLocaleString()}`,
            flag: data.refreshWindow.triggered ? { variant: 'info' as const, label: 'Refresh' } : null,
          },
          ...data.frequencyWarnings.map((f) => ({
            label: `${PLATFORM_LABELS[f.platform]} frequency`,
            value: `${f.frequency.toFixed(2)} (threshold ${f.threshold})`,
            flag: f.triggered ? { variant: 'warn' as const, label: 'Frequency Warning' } : null,
          })),
        ]}
      />

      <SignalCard
        index={5}
        title="Platform Value Score"
        question="Which platform delivered strongest combined result?"
        formula="(Total Impressions × VTR × CTR) ÷ CPM per platform"
        plainLanguage={data.platformValueScore[0]?.plainLanguage ?? 'No data'}
        rows={data.platformValueScore.map((e) => platformRow(e, (v) => v.toLocaleString()))}
      />
    </>
  );
};

function platformRow(e: PlatformSignalEntry, fmt: (v: number) => string) {
  let flag: { variant: 'top' | 'warn' | 'info' | 'muted'; label: string } | null = null;
  if (e.flag === 'recommended') flag = { variant: 'top', label: 'Recommended' };
  else if (e.flag === 'mostEfficient') flag = { variant: 'info', label: 'Most Efficient' };
  else if (e.flag === 'strongestResponse') flag = { variant: 'top', label: 'Strongest Response' };
  return { label: PLATFORM_LABELS[e.platform], value: fmt(e.value), flag };
}

interface RowSpec {
  label: string;
  value: string;
  flag: { variant: 'top' | 'warn' | 'info' | 'muted'; label: string } | null;
}

const SignalCard: React.FC<{
  index: number;
  title: string;
  question: string;
  formula: string;
  plainLanguage: string;
  rows: RowSpec[];
}> = ({ index, title, question, formula, plainLanguage, rows }) => {
  const [showFormula, setShowFormula] = useState(false);
  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <div style={sectionTitle}>Signal {index}</div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h3>
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 2 }}>{question}</div>
        </div>
        <button onClick={() => setShowFormula((s) => !s)} style={ghostBtn}>
          {showFormula ? 'Hide formula' : 'Show formula'}
        </button>
      </div>

      <div style={{
        marginTop: 12, padding: 12, borderRadius: 8,
        background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.20)',
        fontSize: 13, color: 'var(--fg-1)',
      }}>
        <strong style={{ marginRight: 6 }}>Plain language:</strong>{plainLanguage}
      </div>

      {showFormula && (
        <div style={{
          marginTop: 8, padding: 10, borderRadius: 8,
          background: 'var(--bg-app)', border: '1px solid var(--border-1)',
          fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--fg-2)',
        }}>
          {formula}
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 10px', borderRadius: 8,
            background: 'var(--bg-app)', border: '1px solid var(--border-1)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>{r.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: 'var(--fg-2)' }}>{r.value}</span>
              {r.flag && <StatusPill variant={flagToStatus[r.flag.variant]} noDot size="sm">{r.flag.label}</StatusPill>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ghostBtn: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-1)',
  background: 'transparent', color: 'var(--fg-2)',
  fontSize: 11, fontWeight: 500, cursor: 'pointer',
};

export default SignalsView;
